import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";

const IMAGES = {
  gurjan:
    "https://images.unsplash.com/photo-1700973408133-b45276ec8feb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY6NzR8MHwxfHNlYXJjaHwzfHx3b29kJTIwdmVuZWVyJTIwc2hlZXQlMjBncmFpbiUyMGxpZ2h0JTIwdGltYmVyfGVufDB8fHx8MTc5MDYxMzQ2N3ww&ixlib=rb-4.1.0&q=85",
  eucalyptus:
    "https://images.unsplash.com/photo-1776278515712-9d5753884f5f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY6NzV8MHwxfHNlYXJjaHwyfHxldWNhbHlwdHVzJTIwd29vZCUyMHZlbmVlciUyMGdyYWluJTIwbGlnaHQlMjB0aW1iZXJ8ZW58MHx8fHwxNzkwNjEzNDY3fDA&ixlib=rb-4.1.0&q=85",
  okume:
    "https://images.unsplash.com/photo-1737094662085-7e0db16198e3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY6NzR8MHwxfHNlYXJjaHw0fHx3b29kJTIwdmVuZWVyJTIwc2hlZXQlMjBncmFpbiUyMGxpZ2h0JTIwdGltYmVyfGVufDB8fHx8MTc5MDYxMzQ2N3ww&ixlib=rb-4.1.0&q=85",
};

export const ProductsSection = () => {
  const { t } = useLanguage();
  const p = t.products;

  return (
    <section id="products" className="relative py-24 lg:py-32 bg-[#F8FBF8]" data-testid="products-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        >
          <span className="eyebrow-label">{p.eyebrow}</span>
          <h2 className="font-serif-display text-4xl sm:text-5xl tracking-tight mt-4 text-[#12261A] leading-[1.05]">
            {p.title}
          </h2>
        </motion.div>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          {p.items.map((item, i) => (
            <motion.article
              key={item.id}
              initial={{ opacity: 0, y: 36 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.8, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }}
              className="card-sheen bg-white border border-[rgba(20,112,63,0.16)] rounded-sm overflow-hidden hover:border-[rgba(20,112,63,0.4)] transition-colors duration-500 flex flex-col"
              data-testid={`product-card-${item.id}`}
            >
              <div className="h-52 overflow-hidden group">
                <img
                  src={IMAGES[item.id]}
                  alt={item.name}
                  className="w-full h-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-105"
                  loading="lazy"
                  data-testid={`product-image-${item.id}`}
                />
              </div>
              <div className="p-6 lg:p-7 flex flex-col flex-1">
                <h3 className="font-serif-display text-2xl text-[#12261A]" data-testid={`product-name-${item.id}`}>
                  {item.name}
                </h3>
                <p className="text-sm text-[#14703F] mt-1">{item.tag}</p>

                <dl className="mt-6 space-y-3 flex-1" data-testid={`product-specs-${item.id}`}>
                  {[
                    [p.labels.thickness, item.thickness],
                    [p.labels.sizes, item.sizes],
                    [p.labels.type, item.type],
                    [p.labels.use, item.use],
                  ].map(([k, v]) => (
                    <div key={k} className="flex gap-4 border-t border-[rgba(20,112,63,0.12)] pt-3">
                      <dt className="font-mono-spec text-[9px] tracking-[0.18em] uppercase text-[#8CA594] w-24 shrink-0 pt-0.5">{k}</dt>
                      <dd className="text-sm text-[#12261A] leading-snug">{v}</dd>
                    </div>
                  ))}
                </dl>

                <a
                  href="#enquiry"
                  data-testid={`product-enquire-${item.id}`}
                  className="mt-6 inline-flex items-center gap-2 font-mono-spec text-[10px] tracking-[0.2em] uppercase text-[#14703F] hover:gap-3 transition-[gap] duration-300"
                >
                  {p.enquire}
                  <ArrowUpRight size={13} />
                </a>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
};
